// Workflow Dashboard Page - AIAG-VDA Step 7 Approval Gates
// Displays items pending review/approval with approval signature tracking
// ============================================================

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Clock,
    CheckCircle,
    FileText,
    AlertTriangle,
    RefreshCw,
    Shield,
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useAppContext } from '../contexts/AppContext';
import clsx from 'clsx';
import {
    PfmeaItem,
    ApprovalSignature,
} from '../types';
import {
    getPfmeaItems,
    submitForReview,
    approveItem,
    rejectItem,
    calculateRPN,
} from '../services/pfmeaService';

import StatCard from '../components/Workflow/StatCard';
import TabButton from '../components/Workflow/TabButton';
import PfmeaListItem from '../components/Workflow/PfmeaListItem';
import ItemDetailSidebar from '../components/Workflow/ItemDetailSidebar';
import ApprovalModal from '../components/Workflow/ApprovalModal';
import DraftItemsList from '../components/Workflow/DraftItemsList';

// --- Workflow Dashboard Page ---

const WorkflowDashboardPage: React.FC = () => {
    const { theme } = useTheme();
    const { user, hasPermission } = useAuth();
    const { activeProject, activeProjectName } = useAppContext();
    const navigate = useNavigate();
    const isDark = theme === 'dark';

    // State
    const [items, setItems] = useState<PfmeaItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<'pending' | 'approved' | 'all'>('pending');
    const [selectedItem, setSelectedItem] = useState<PfmeaItem | null>(null);
    const [showApprovalModal, setShowApprovalModal] = useState(false);
    const [approvalAction, setApprovalAction] = useState<'approve' | 'reject' | null>(null);
    const [approvalComment, setApprovalComment] = useState('');
    const [processing, setProcessing] = useState(false);

    // Get PFMEA items from AppContext
    const { pfmeaItems: contextPfmeaItems } = useAppContext();

    // Load data from context (fallback from API)
    useEffect(() => {
        if (activeProjectName) {
            loadData();
        }
    }, [activeProjectName, contextPfmeaItems]);

    const loadData = async () => {
        if (!activeProjectName) return;
        setLoading(true);
        setError(null);
        try {
            // Try API first
            const data = await getPfmeaItems(activeProjectName);
            setItems(data);
        } catch (err: any) {
            console.warn('API not available, using local data:', err.message);
            // Fallback to context data - convert to service PfmeaItem type
            const localItems = contextPfmeaItems.map(item => ({
                ...item,
                projectName: activeProjectName || '',
                failureEffect: (item as any).failureEffect || (item as any).effectLocal || '',
            })) as unknown as PfmeaItem[];
            setItems(localItems);
            setError(null); // Clear error since we have fallback data
        } finally {
            setLoading(false);
        }
    };

    // Categorized items
    const categorizedItems = useMemo(() => {
        const draft = items.filter(i => i.status === 'draft');
        const review = items.filter(i => i.status === 'review');
        const approved = items.filter(i => i.status === 'approved');
        return { draft, review, approved };
    }, [items]);

    // Filtered items based on active tab
    const filteredItems = useMemo(() => {
        switch (activeTab) {
            case 'pending':
                return categorizedItems.review;
            case 'approved':
                return categorizedItems.approved;
            default:
                return items;
        }
    }, [activeTab, categorizedItems, items]);

    // Stats
    const stats = useMemo(() => ({
        total: items.length,
        draft: categorizedItems.draft.length,
        pendingReview: categorizedItems.review.length,
        approved: categorizedItems.approved.length,
        highPriorityPending: categorizedItems.review.filter(i => i.actionPriority === 'H').length,
    }), [items, categorizedItems]);

    // Handlers
    const handleSubmitForReview = async (item: PfmeaItem) => {
        if (!confirm('Submit this item for review?')) return;
        setProcessing(true);
        try {
            const updated = await submitForReview(item.id);
            setItems(prev => prev.map(i => i.id === item.id ? updated : i));
        } catch (err: any) {
            alert('Error: ' + err.message);
        } finally {
            setProcessing(false);
        }
    };

    const handleApprovalClick = (item: PfmeaItem, action: 'approve' | 'reject') => {
        setSelectedItem(item);
        setApprovalAction(action);
        setApprovalComment('');
        setShowApprovalModal(true);
    };

    const handleConfirmApproval = async () => {
        if (!selectedItem || !approvalAction) return;
        if (approvalAction === 'reject' && !approvalComment.trim()) {
            alert('Please provide a reason for rejection.');
            return;
        }

        setProcessing(true);
        try {
            let updated: PfmeaItem;
            if (approvalAction === 'approve') {
                updated = await approveItem(selectedItem.id, approvalComment || undefined);
            } else {
                updated = await rejectItem(selectedItem.id, approvalComment);
            }
            setItems(prev => prev.map(i => i.id === selectedItem.id ? updated : i));
            setShowApprovalModal(false);
            setSelectedItem(null);
        } catch (err: any) {
            alert('Error: ' + err.message);
        } finally {
            setProcessing(false);
        }
    };

    const handleViewDetails = (item: PfmeaItem) => {
        setSelectedItem(item);
    };

    // Format date
    const formatDate = (dateString?: string) => {
        if (!dateString) return '—';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    // Get last action from change history
    const getLastAction = (item: PfmeaItem): ApprovalSignature | null => {
        if (!item.changeHistory || item.changeHistory.length === 0) return null;
        const statusChanges = item.changeHistory.filter((c: any) => c.field === 'status');
        if (statusChanges.length === 0) return null;
        const last = statusChanges[statusChanges.length - 1];
        return {
            action: last.newValue as 'submitted' | 'approved' | 'rejected',
            user: last.user,
            timestamp: last.timestamp,
            comment: last.comment,
        };
    };

    // No project selected
    if (!activeProject || activeProject === 'Select Project') {
        return (
            <div className="flex flex-col items-center justify-center h-96 text-center">
                <Shield size={64} className="text-slate-300 mb-4" />
                <h2 className={clsx('text-xl font-semibold mb-2', isDark ? 'text-white' : 'text-slate-800')}>
                    No Project Selected
                </h2>
                <p className="text-slate-500 mb-4">Select a project to view workflow status.</p>
                <button
                    onClick={() => navigate('/projects')}
                    className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
                >
                    Go to Projects
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                    <h1 className={clsx('text-2xl font-bold flex items-center gap-2', isDark ? 'text-white' : 'text-slate-800')}>
                        <Shield className="text-primary-500" />
                        Workflow Dashboard
                    </h1>
                    <p className="text-slate-500 text-sm">
                        AIAG-VDA Step 7: Approval Gates & Signatures · {activeProject}
                    </p>
                </div>
                <button
                    onClick={loadData}
                    disabled={loading}
                    className="flex items-center gap-2 px-4 py-2 bg-slate-100 dark:bg-slate-700 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600"
                >
                    <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                    Refresh
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <StatCard isDark={isDark} icon={FileText} value={stats.draft} label="Draft Items" />
                <StatCard
                    isDark={isDark}
                    icon={Clock}
                    value={stats.pendingReview}
                    label="Pending Review"
                    colorClass={isDark ? 'bg-blue-900/30 border-blue-800' : 'bg-blue-50 border-blue-200'}
                    subLabel={stats.highPriorityPending > 0 ? (
                        <div className="flex items-center gap-1 text-red-500"><AlertTriangle size={12} />{stats.highPriorityPending} high priority</div>
                    ) : null}
                />
                <StatCard
                    isDark={isDark}
                    icon={CheckCircle}
                    value={stats.approved}
                    label="Approved"
                    colorClass={isDark ? 'bg-green-900/30 border-green-800' : 'bg-green-50 border-green-200'}
                />
                <StatCard
                    isDark={isDark}
                    icon={Shield}
                    value={`${stats.total > 0 ? Math.round((stats.approved / stats.total) * 100) : 0}%`}
                    label="Approval Rate"
                />
            </div>

            <div className={clsx('flex rounded-lg p-1 w-fit', isDark ? 'bg-slate-800' : 'bg-slate-100')}>
                {[
                    { key: 'pending', label: 'Pending Review', count: stats.pendingReview, icon: Clock },
                    { key: 'approved', label: 'Approved', count: stats.approved, icon: CheckCircle },
                    { key: 'all', label: 'All Items', count: stats.total, icon: FileText },
                ].map(tab => (
                    <TabButton key={tab.key} tab={tab} activeTab={activeTab} onClick={(k) => setActiveTab(k as any)} />
                ))}
            </div>

            {/* Items List */}
            <div className={clsx(
                'rounded-xl border overflow-hidden',
                isDark ? 'border-slate-700' : 'border-slate-200'
            )}>
                {loading ? (
                    <div className="p-12 text-center text-slate-500">Loading...</div>
                ) : error ? (
                    <div className="p-12 text-center text-red-500">{error}</div>
                ) : filteredItems.length === 0 ? (
                    <div className="p-12 text-center">
                        <CheckCircle size={48} className="mx-auto text-green-300 mb-4" />
                        <p className={clsx('text-lg font-medium', isDark ? 'text-white' : 'text-slate-800')}>
                            {activeTab === 'pending' ? 'No items pending review!' : 'No items found'}
                        </p>
                        <p className="text-sm text-slate-500">
                            {activeTab === 'pending' ? 'All items have been reviewed.' : 'Try adjusting your filters.'}
                        </p>
                    </div>
                ) : (
                    <div className="divide-y divide-slate-200 dark:divide-slate-700">
                        {filteredItems.map(item => (
                            <PfmeaListItem
                                key={item.id}
                                item={item}
                                isDark={isDark}
                                hasPermission={hasPermission}
                                processing={processing}
                                calculateRPN={calculateRPN}
                                formatDate={formatDate}
                                getLastAction={getLastAction}
                                onSubmit={handleSubmitForReview}
                                onApproveClick={handleApprovalClick}
                                onViewDetails={handleViewDetails}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Draft Items Section (if on pending tab, show drafts too) */}
            {activeTab === 'pending' && categorizedItems.draft.length > 0 && (
                <DraftItemsList
                    draftItems={categorizedItems.draft}
                    isDark={isDark}
                    processing={processing}
                    onSubmit={handleSubmitForReview}
                />
            )}

            {!showApprovalModal && (
                <ItemDetailSidebar
                    selectedItem={selectedItem}
                    isDark={isDark}
                    formatDate={formatDate}
                    onClose={() => setSelectedItem(null)}
                />
            )}

            {showApprovalModal && (
                <ApprovalModal
                    isDark={isDark}
                    item={selectedItem}
                    action={approvalAction}
                    comment={approvalComment}
                    onCommentChange={setApprovalComment}
                    onClose={() => setShowApprovalModal(false)}
                    onConfirm={handleConfirmApproval}
                    processing={processing}
                    userEmail={user?.email || undefined}
                />
            )}
        </div>
    );
};

export default WorkflowDashboardPage;
